import { brandOffersCustomCharacter } from '@/lib/brands'
import { isCustomCharacterId } from '@/server/domain/custom-character'

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'y'])
const FALSE_VALUES = new Set(['0', 'false', 'no', 'n'])

/**
 * Exportado porque hay flags que NO pueden ser `NEXT_PUBLIC_` (decisiones por
 * petición, ADR-020) y viven en su módulo de política del lado servidor. El
 * contrato de parseo —qué cuenta como sí, qué como no, y que un valor ilegible
 * cae al default en vez de tumbar nada— tiene que ser el mismo en los dos sitios.
 */
export function parseBooleanEnv(value: string | undefined, defaultValue: boolean): boolean {
  if (!value) return defaultValue

  const normalized = value.trim().toLowerCase()
  if (TRUE_VALUES.has(normalized)) return true
  if (FALSE_VALUES.has(normalized)) return false
  return defaultValue
}

export function isVideoCallsPaused(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_VIDEO_CALLS_PAUSED, false)
}

export function isGoogleSignInEnabled(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_GOOGLE_SIGN_IN_ENABLED, false)
}

export function isAppleSignInEnabled(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_APPLE_SIGN_IN_ENABLED, false)
}

export function isDashboardExampleVideoEnabled(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_DASHBOARD_EXAMPLE_VIDEO_ENABLED, false)
}

/**
 * Interruptor público de la landing del personaje propio (`/tu-personaje`).
 *
 * La feature real la gobierna el kill-switch server-only
 * `CUSTOM_CHARACTER_ENABLED` (server/application/custom-character-flags.ts),
 * que no viaja al bundle. Esta env solo puede encenderse con
 * `CUSTOM_CHARACTER_ENABLED` activo (no al revés): nunca una landing indexable
 * —con FAQ y precios— vendiendo algo que el dashboard no enseña. Default
 * false: la landing no sale hasta que se decida publicarla.
 */
export function isCustomCharacterLandingEnabled(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_CUSTOM_CHARACTER_LANDING_ENABLED, false)
}

/**
 * ¿Sirve ESTE despliegue `/tu-personaje`? La env de arriba y además que la marca
 * ofrezca el personaje propio (ADR-035). Es la pregunta de la página, el sitemap,
 * el back-link del auth y el cross-sell. Los footers que enlazan la landing de
 * papanoel.chat desde otra marca preguntan solo por la env: allí la landing existe.
 */
export function servesCustomCharacterLanding(): boolean {
  return brandOffersCustomCharacter() && isCustomCharacterLandingEnabled()
}

/** Master kill-switch de la prueba gratuita, por encima de cualquier scope. */
export function isTrialEnabled(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_TRIAL_ENABLED, true)
}

export type TrialScope = 'audio' | 'all'
export type TrialCallType = 'audio' | 'video'

/**
 * Modalidades que pueden consumir la prueba gratuita.
 *
 * `audio` es el default de producto y el modo barato: incluye las variantes de
 * audio ya existentes y excluye todo vídeo, también los genéricos LemonSlice.
 * `all` conserva el trial universal anterior como rollback operativo. Es
 * `NEXT_PUBLIC_` porque la misma decisión debe quedar congelada en cliente y
 * servidor durante un despliegue; cambiarla requiere redeploy.
 */
export function getTrialScope(): TrialScope {
  return process.env.NEXT_PUBLIC_TRIAL_SCOPE?.trim().toLowerCase() === 'all'
    ? 'all'
    : 'audio'
}

/** Veredicto compartido por la disponibilidad cliente y el gate servidor. */
export function isTrialEnabledForCallType(callType: TrialCallType): boolean {
  if (!isTrialEnabled()) return false
  return getTrialScope() === 'all' || callType === 'audio'
}

/** La UI lo usa para explicar por qué el vídeo muestra una entrada de compra. */
export function isAudioOnlyTrialEnabled(): boolean {
  return isTrialEnabled() && getTrialScope() === 'audio'
}

/**
 * Habilita el **pack de 1 minuto de pago** (`trial_1min`): la oferta de rescate
 * que `PackageSelector` muestra a quien duda ante los packs, y su checkout.
 *
 * ⚠️ NO confundir con `isTrialEnabled()` (`NEXT_PUBLIC_TRIAL_ENABLED`), que es el
 * kill-switch de la llamada de prueba **gratis**. Son features independientes
 * aunque partes del código llamen "trial" a esta compra (`trial_1min`,
 * `/api/payments/trial/checkout`): una regala una llamada corta, la otra vende un
 * minuto. Cada flag mueve solo la suya.
 *
 * Default **false**: hasta hace poco era un `const TRIAL_ENABLED = false` en
 * `package-selector.tsx`, y el default preserva ese comportamiento. Lo leen el
 * cliente (visibilidad de la oferta) y el servidor (gate autoritativo en
 * `PaymentService.createTrialCheckoutSession`, para que un POST directo al
 * endpoint no compre un pack que el producto tiene apagado). Build-time: flip =
 * redeploy. Requiere además `DODO_PRODUCT_1MIN` (un solo producto por pack,
 * ADR-021 regla 4).
 */
export function isOneMinutePackEnabled(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_ONE_MINUTE_PACK_ENABLED, false)
}

export type PostTrialCheckoutVariant = 'package_selector' | 'post_trial_single_offer'

/**
 * Variante de presentación del checkout que se abre después de la prueba.
 *
 * Este flag NO concede el pack de 1 minuto: solo pide presentar esa oferta en
 * lugar del catálogo. La cuenta y el endpoint siguen obedeciendo la política
 * autoritativa (`oneMinutePackOffer`), incluido el kill-switch, país, compra
 * previa y handoff reciente. Si cualquiera de esas puertas falla, el cliente
 * degrada al selector actual.
 *
 * Default control. Es `NEXT_PUBLIC_`, por lo que Next lo fija en build-time y
 * cambiarlo requiere redeploy.
 */
export function getPostTrialCheckoutVariant(): PostTrialCheckoutVariant {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_POST_TRIAL_SINGLE_OFFER_ENABLED, false)
    ? 'post_trial_single_offer'
    : 'package_selector'
}

// A QUIÉN se le enseña el pack (rescate vs catálogo) NO se decide aquí: es
// server-only, junto al resto de la política del pack, en
// `server/application/one-minute-pack-policy.ts`. El cliente recibe un único
// veredicto (`AccountDto.oneMinutePackOffer`) y lo obedece.

/**
 * Contrato común de los flags NEXT_PUBLIC_<CHAR>_VIDEO_PROVIDER: solo un valor
 * de la lista explícita de proveedores conmuta; cualquier otro (o ausente) cae
 * al proveedor legacy del personaje. Build-time (Next inlinea cada literal
 * process.env.NEXT_PUBLIC_* — por eso cada getter conserva el suyo — y
 * cambiarlos requiere redeploy).
 */
function asProviderOr<P extends string, T extends string>(
  raw: string | undefined,
  providers: readonly P[],
  fallback: T
): P | T {
  const normalized = raw?.trim().toLowerCase()
  const match = providers.find((provider) => provider === normalized)
  return match ?? fallback
}

/** Transportes reales de las páginas de vídeo dedicadas (ADR-012). */
const VIDEO_TRANSPORTS = ['runway', 'livekit'] as const
type VideoTransport = (typeof VIDEO_TRANSPORTS)[number]

function asTransportOr<T extends string>(raw: string | undefined, legacy: T): VideoTransport | T {
  return asProviderOr(raw, VIDEO_TRANSPORTS, legacy)
}

/**
 * Colapsa el flag de un personaje al transporte de su página dedicada. El
 * mapeo legacy→runway no es un descuido: el trial fuerza la página dedicada
 * aunque el flag siga en el legacy (HeyGen/ANAM no cortan la sesión
 * server-side a los segundos del trial; runway y livekit sí), así que el enum
 * de 3 valores del flag colapsa aquí a los 2 transportes reales.
 */
function toVideoTransport(provider: string): VideoTransport {
  return provider === 'livekit' ? 'livekit' : 'runway'
}

export type SantaVideoProvider = 'heygen' | VideoTransport

/**
 * heygen = /child → /call actual; runway y livekit = /child-santa-video (la
 * página elige el transporte según este mismo flag, ADR-012).
 */
export function getSantaVideoProvider(): SantaVideoProvider {
  return asTransportOr(process.env.NEXT_PUBLIC_SANTA_VIDEO_PROVIDER, 'heygen')
}

/** Transporte de la página /child-santa-video (ver toVideoTransport). */
export function getSantaVideoTransport(): VideoTransport {
  return toVideoTransport(getSantaVideoProvider())
}

export type ToothFairyVideoProvider = 'anam' | VideoTransport

/**
 * anam = /child-tooth-fairy-video actual; runway y livekit =
 * /child-tooth-fairy-video-runway (la página elige el transporte, ADR-012).
 */
export function getToothFairyVideoProvider(): ToothFairyVideoProvider {
  return asTransportOr(process.env.NEXT_PUBLIC_TOOTH_FAIRY_VIDEO_PROVIDER, 'anam')
}

/** Transporte de la página /child-tooth-fairy-video-runway (ver toVideoTransport). */
export function getToothFairyVideoTransport(): VideoTransport {
  return toVideoTransport(getToothFairyVideoProvider())
}

export type ReyesVideoProvider = 'anam' | VideoTransport

/**
 * melchor-video y baltasar-video conmutan juntos: anam = /child-reyes actual;
 * runway y livekit = /child-{melchor,baltasar}-video-runway (cada página
 * elige el transporte, ADR-012).
 */
export function getReyesVideoProvider(): ReyesVideoProvider {
  return asTransportOr(process.env.NEXT_PUBLIC_REYES_VIDEO_PROVIDER, 'anam')
}

/** Transporte de las páginas /child-{melchor,baltasar}-video-runway (ver toVideoTransport). */
export function getReyesVideoTransport(): VideoTransport {
  return toVideoTransport(getReyesVideoProvider())
}

export type RatonPerezVideoProvider = VideoTransport

/**
 * A diferencia del resto, el Ratón Pérez no tiene camino legacy (HeyGen/ANAM):
 * /child-raton-perez-video ES su página por defecto, así que este flag no
 * conmuta rutas — solo el transporte de esa página (ADR-012).
 */
export function getRatonPerezVideoProvider(): RatonPerezVideoProvider {
  return asTransportOr(process.env.NEXT_PUBLIC_RATON_PEREZ_VIDEO_PROVIDER, 'runway')
}

/** Sin legacy que colapsar: el flag del Ratón ya ES el transporte. */
export const getRatonPerezVideoTransport = getRatonPerezVideoProvider

/**
 * Characters whose video calls stay enabled even while video is globally paused.
 * These have their avatar provider provisioned and are launched ahead of the
 * rest (Santa, Melchor, and Baltasar remain gated by NEXT_PUBLIC_VIDEO_CALLS_PAUSED).
 *
 * ⚠️ Copy coupling: the "which characters are paused vs live" split is also
 * stated in prose in the paused-video banners — `videoPausedNotice`
 * (character-selector) and `calls.videoPausedBanner` (dashboard) in
 * locales/{es,en,pt,it,fr,ja,de}. JSON can't hold a back-reference comment, so when you add
 * or remove an id here you MUST update those banner titles/descriptions in all
 * supported languages. `tests/unit/lib/video-pause-banner-copy.test.ts` fails CI if
 * the banner titles drift out of sync with this set.
 */
export const VIDEO_PAUSE_EXEMPT_CHARACTER_IDS = new Set([
  'tooth-fairy-video',
  'raton-perez-video',
  'gaspar-video',
])

/**
 * Personajes LiveKit/LemonSlice on-the-fly: el catálogo genérico (prefijo
 * `generic-`) y el personaje propio (`CUSTOM_CHARACTER_ID`). Ninguno depende
 * de los proveedores pausables, así que ninguno se pausa con ellos — un solo
 * predicado con nombre en vez de dos ramas que dicen lo mismo.
 */
function isLiveKitOnTheFlyCharacter(characterId: string): boolean {
  return characterId.startsWith('generic-') || isCustomCharacterId(characterId)
}

/**
 * Checks if video calls are paused for a specific character.
 * Exempt characters (see VIDEO_PAUSE_EXEMPT_CHARACTER_IDS) are always enabled,
 * even when other video calls are paused.
 * @param characterId - The character ID to check
 * @returns true if video calls are paused for this character, false otherwise
 */
export function isVideoCallPausedForCharacter(characterId: string): boolean {
  if (VIDEO_PAUSE_EXEMPT_CHARACTER_IDS.has(characterId)) {
    return false
  }
  if (isLiveKitOnTheFlyCharacter(characterId)) {
    return false
  }

  // For all other characters, check if video calls are globally paused
  return isVideoCallsPaused()
}

/** POC clip length — not an env flag (design 2026-07-19). */
export const MEMORY_VIDEO_CLIP_SECONDS = 30

/**
 * Kill-switch del vídeo recuerdo (POC client-side). Default false: sin UI ni
 * recorder cuando la env está ausente. Build-time (NEXT_PUBLIC_*).
 */
export function isMemoryVideoEnabled(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_MEMORY_VIDEO_ENABLED, false)
}

/**
 * Default del toggle “crear recuerdo” cuando el feature está on. Default true
 * (viralidad). Solo aplica si isMemoryVideoEnabled().
 */
export function isMemoryVideoDefaultOn(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_MEMORY_VIDEO_DEFAULT_ON, true)
}

/**
 * Default del sub-toggle “incluir cámara del niño”. Default false (privacidad).
 */
export function isMemoryVideoIncludeChildDefault(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_MEMORY_VIDEO_INCLUDE_CHILD_DEFAULT, false)
}

/**
 * Pintar watermark PapaNoel.chat en el canvas del clip. Default true.
 */
export function isMemoryVideoWatermarkEnabled(): boolean {
  return parseBooleanEnv(process.env.NEXT_PUBLIC_MEMORY_VIDEO_WATERMARK_ENABLED, true)
}
